import { z } from 'zod';

/**
 * Avatar appearance. Every field is a palette or style index; the client's
 * wardrobe decides what each index looks like, and the wardrobe catalogue
 * (wardrobe.ts) says which ones cost Kaki Dollars to unlock.
 *
 * Ranges are deliberately wider than today's options so new styles can ship
 * without a protocol change. Index 0 is always a valid default, and for `hat`
 * and `glasses` it means "none".
 */
export const LOOK_LIMITS = {
  body: 8,
  eyes: 8,
  hair: 32,
  hairColor: 16,
  top: 32,
  topColor: 24,
  bottom: 16,
  bottomColor: 24,
  shoes: 8,
  shoesColor: 16,
  hat: 32,
  glasses: 16,
  /** Held props (lightsabers and the like); 0 means "holding nothing". */
  prop: 8,
} as const;

export type LookSlot = keyof typeof LOOK_LIMITS;
export const LOOK_SLOTS = Object.keys(LOOK_LIMITS) as LookSlot[];

const index = (limit: number) => z.number().int().min(0).max(limit - 1);

export const lookSchema = z.object({
  body: index(LOOK_LIMITS.body),
  eyes: index(LOOK_LIMITS.eyes),
  hair: index(LOOK_LIMITS.hair),
  hairColor: index(LOOK_LIMITS.hairColor),
  top: index(LOOK_LIMITS.top),
  topColor: index(LOOK_LIMITS.topColor),
  bottom: index(LOOK_LIMITS.bottom),
  bottomColor: index(LOOK_LIMITS.bottomColor),
  shoes: index(LOOK_LIMITS.shoes),
  shoesColor: index(LOOK_LIMITS.shoesColor),
  hat: index(LOOK_LIMITS.hat),
  glasses: index(LOOK_LIMITS.glasses),
  prop: index(LOOK_LIMITS.prop),
  /** A player-made design printed on the top, by design id; null for plain. */
  print: z.string().min(1).max(64).nullable(),
});

export type Look = z.infer<typeof lookSchema>;

export const DEFAULT_LOOK: Look = Object.freeze({
  body: 1,
  eyes: 0,
  hair: 0,
  hairColor: 0,
  top: 0,
  topColor: 0,
  bottom: 0,
  bottomColor: 0,
  shoes: 0,
  shoesColor: 0,
  hat: 0,
  glasses: 0,
  prop: 0,
  print: null,
});

/**
 * Accepts any stored or submitted value and returns a valid Look.
 *
 * Handles the original three-field format ({ body, shirt, hair }), in which a
 * hair index chose both style and colour, the trousers matched the shirt, and
 * hair style 5 was a cap. Unknown or out-of-range fields fall back to defaults.
 */
export function normaliseLook(raw: unknown): Look {
  if (!raw || typeof raw !== 'object') return { ...DEFAULT_LOOK };
  const input = raw as Record<string, unknown>;
  const pick = (key: LookSlot, fallback: number): number => {
    const value = Number(input[key]);
    if (!Number.isInteger(value) || value < 0 || value >= LOOK_LIMITS[key]) return fallback;
    return value;
  };

  const legacy = 'shirt' in input && !('topColor' in input);
  if (legacy) {
    const shirt = pick('topColor', Number(input.shirt) || 0);
    const hair = pick('hair', 0);
    return {
      ...DEFAULT_LOOK,
      body: pick('body', DEFAULT_LOOK.body),
      hair,
      hairColor: hair,
      topColor: Math.min(shirt, LOOK_LIMITS.topColor - 1),
      bottomColor: Math.min(shirt, LOOK_LIMITS.bottomColor - 1),
      hat: hair === 5 ? 1 : 0,
    };
  }

  const print = typeof input.print === 'string' && input.print.length > 0 && input.print.length <= 64 ? input.print : null;
  const look = { print } as Look;
  for (const slot of LOOK_SLOTS) look[slot] = pick(slot, DEFAULT_LOOK[slot]);
  return look;
}

export function looksEqual(a: Look, b: Look): boolean {
  return a.print === b.print && LOOK_SLOTS.every((slot) => a[slot] === b[slot]);
}
